import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../../utils/httpClientTs';
import { getMyPermissions } from '../../../endpoints/usersTs';
import { hasUserApplicationPermission } from '../../../utils/permissions';
import {
  completeFoundationStudentVerification,
  finalApproveFoundationApplication,
  getFoundationApplication,
  patchFoundationApplication,
  postFoundationReviewAction,
  putFoundationInstitution,
  resolveFoundationTrusteeHold,
  saveFoundationACH,
  searchFoundationInstitutions,
  submitFoundationRecommendation,
  toFormAnswerInputs,
  uploadFoundationEnrollmentDocument,
  type FoundationApplicationDetail,
  type FoundationFormAnswerInput,
  type FoundationInstitutionSearchResult,
  type FoundationStudentContact,
} from '../../../endpoints/foundation';
import PageHeader from '../../../Components/PageHeader';
import StudentContactFields from './StudentContactFields';
import FoundationFormRenderer from './FoundationFormRenderer';

function contactFromApp(
  app: FoundationApplicationDetail
): FoundationStudentContact {
  return {
    student_address_1: app.student_address_1 || '',
    student_address_2: app.student_address_2 || '',
    student_city: app.student_city || '',
    student_state_id: app.student_state_id || '',
    student_postal_code: app.student_postal_code || '',
    student_country_id: app.student_country_id || '',
    student_primary_phone: app.student_primary_phone || '',
    student_primary_phone_type: app.student_primary_phone_type || '',
    student_secondary_phone: app.student_secondary_phone || '',
    student_secondary_phone_type: app.student_secondary_phone_type || '',
  };
}

export default function FoundationApplicantDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [institutionQuery, setInstitutionQuery] = useState('');
  const [installmentCount, setInstallmentCount] = useState('1');
  const [date1, setDate1] = useState('');
  const [date2, setDate2] = useState('');
  const [payeeName, setPayeeName] = useState('');
  const [achLast4, setAchLast4] = useState('');
  const [achPlaintext, setAchPlaintext] = useState('');
  const [achToken, setAchToken] = useState('');
  const [contact, setContact] = useState<FoundationStudentContact>({});
  const [recAnswers, setRecAnswers] = useState<FoundationFormAnswerInput[]>([]);
  const [studentAnswers, setStudentAnswers] = useState<
    FoundationFormAnswerInput[]
  >([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const { data: permissions } = useApiGet<string[]>(
    ['my-permissions-foundation-detail'],
    () => getMyPermissions({ international: '1' }),
    { enabled: true }
  );
  const canRecommend = hasUserApplicationPermission(
    permissions,
    'foundation:recommend'
  );
  const canReview = hasUserApplicationPermission(
    permissions,
    'foundation:review'
  );
  const canManage = hasUserApplicationPermission(
    permissions,
    'foundation:manage'
  );

  const {
    data: app,
    isLoading,
    refetch,
  } = useApiGet<FoundationApplicationDetail>(
    ['foundation-application', id],
    () => getFoundationApplication(id),
    { enabled: !!id, retry: 1 }
  );

  const { data: searchData } = useApiGet<{
    items: FoundationInstitutionSearchResult[];
  }>(
    ['foundation-institution-search', institutionQuery],
    () => searchFoundationInstitutions(institutionQuery),
    { enabled: institutionQuery.trim().length >= 2, retry: 0 }
  );

  useEffect(() => {
    if (!app) return;
    setContact(contactFromApp(app));
    setRecAnswers(toFormAnswerInputs(app.answers_by_form_kind?.recommendation));
    setStudentAnswers(
      toFormAnswerInputs(app.answers_by_form_kind?.student_verification)
    );
  }, [app]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e: any) {
      setError(e?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (isLoading || !app) {
    return (
      <Container maxWidth="md" sx={{ py: 2 }}>
        <Typography>Loading…</Typography>
      </Container>
    );
  }

  const amount = Number(app.awarded_amount || 0);

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <PageHeader
        title={
          `${app.student_first_name || ''} ${app.student_last_name || ''}`.trim() ||
          'Application'
        }
        leftDom={undefined}
        rightDom={undefined}
        subtitle={undefined}
      />
      <Button sx={{ mb: 2 }} onClick={() => navigate('/foundation/applicants')}>
        Back to applicants
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography>
          <strong>Stage:</strong> {app.stage}
        </Typography>
        <Typography>
          <strong>Year:</strong> {app.academic_year}
        </Typography>
        <Typography>
          <strong>Amount:</strong> {app.currency} {amount.toFixed(2)}
        </Typography>
        <Typography>
          <strong>Email:</strong> {app.student_email || '—'}
        </Typography>
        {(app.student_primary_phone || app.student_city) && (
          <Typography variant="body2">
            Contact:{' '}
            {[app.student_primary_phone, app.student_city]
              .filter(Boolean)
              .join(' · ') || '—'}
          </Typography>
        )}
        {app.gates && (
          <Typography variant="body2" color="text.secondary">
            Gates — eligibility: {app.gates.eligibility_gate_status}, student:{' '}
            {app.gates.student_verification_status}, trustee:{' '}
            {app.gates.trustee_review_status}, EO: {app.gates.io_review_status},
            enrollment: {app.gates.enrollment_status}
          </Typography>
        )}
        {app.institution && (
          <Typography variant="body2">
            Institution: {app.institution.match_status}
            {app.institution.provisional_name
              ? ` (${app.institution.provisional_name})`
              : ''}
          </Typography>
        )}
        {(app.commitments || []).map((c) => (
          <Typography key={c.id} variant="body2">
            Commitment {c.fund_id}: {c.amount} ({c.status})
          </Typography>
        ))}
      </Stack>

      {canRecommend &&
        (app.stage === 'draft' || app.stage === 'recommendation_submitted') && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Divider />
            <Typography variant="h6">Edit recommendation</Typography>
            <StudentContactFields
              value={contact}
              onChange={setContact}
              disabled={busy || app.stage !== 'draft'}
            />
            <FoundationFormRenderer
              formKind="recommendation"
              academicYear={app.academic_year}
              value={recAnswers}
              onChange={setRecAnswers}
              applicationId={id}
              disabled={busy || app.stage !== 'draft'}
              title="Recommendation form"
            />
            {app.stage === 'draft' && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      patchFoundationApplication(id, {
                        ...contact,
                        answers: recAnswers,
                      })
                    )
                  }
                >
                  Save draft changes
                </Button>
                <Button
                  variant="contained"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await patchFoundationApplication(id, {
                        ...contact,
                        answers: recAnswers,
                      });
                      await submitFoundationRecommendation(id);
                    })
                  }
                >
                  Save & submit recommendation
                </Button>
              </Stack>
            )}
          </Stack>
        )}

      {canReview && app.stage === 'gate_trustee_hold' && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() =>
              run(() => resolveFoundationTrusteeHold(id, { approve: true }))
            }
          >
            Override gate
          </Button>
          <Button
            color="error"
            disabled={busy}
            onClick={() =>
              run(() => resolveFoundationTrusteeHold(id, { approve: false }))
            }
          >
            Deny
          </Button>
        </Stack>
      )}

      {canReview && app.stage === 'parallel_review' && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Divider />
          <Typography variant="h6">Review</Typography>
          <TextField
            label="Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            minRows={2}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              disabled={busy}
              onClick={() =>
                run(() =>
                  postFoundationReviewAction(id, {
                    action_type: 'comment',
                    body: comment,
                  })
                )
              }
            >
              Add comment
            </Button>
            <Button
              variant="contained"
              disabled={busy}
              onClick={() =>
                run(() =>
                  postFoundationReviewAction(id, {
                    action_type: 'trustee_approve',
                    body: comment,
                  })
                )
              }
            >
              Trustee approve
            </Button>
            <Button
              variant="contained"
              disabled={busy}
              onClick={() =>
                run(() =>
                  postFoundationReviewAction(id, {
                    action_type: 'eo_approve',
                    body: comment,
                  })
                )
              }
            >
              EO approve
            </Button>
            <Button
              disabled={busy}
              onClick={() =>
                run(() =>
                  completeFoundationStudentVerification(id, {
                    answers: studentAnswers,
                  })
                )
              }
            >
              Mark student verified
            </Button>
          </Stack>
          <FoundationFormRenderer
            formKind="student_verification"
            academicYear={app.academic_year}
            value={studentAnswers}
            onChange={setStudentAnswers}
            applicationId={id}
            disabled={busy}
            title="Student verification form"
          />
          <TextField
            label="Search accredited institutions"
            value={institutionQuery}
            onChange={(e) => setInstitutionQuery(e.target.value)}
          />
          {(searchData?.items || []).slice(0, 8).map((inst) => (
            <Button
              key={inst.id}
              size="small"
              disabled={busy}
              onClick={() =>
                run(() =>
                  putFoundationInstitution(id, {
                    match_status: 'matched',
                    accredited_institution_id: inst.id,
                  })
                )
              }
            >
              Match: {inst.name}
            </Button>
          ))}
          <Button
            disabled={busy}
            onClick={() =>
              run(() =>
                putFoundationInstitution(id, {
                  match_status: 'exception_approved',
                  exception_reason: 'Trustee exception',
                })
              )
            }
          >
            Approve institution exception
          </Button>
        </Stack>
      )}

      {canManage &&
        (app.stage === 'post_award_docs' ||
          app.stage === 'final_io_approval') && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Divider />
            <Typography variant="h6">Post-award docs & ACH</Typography>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload enrollment document
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                run(() => uploadFoundationEnrollmentDocument(id, file));
                e.target.value = '';
              }}
            />
            <TextField
              label="ACH last4"
              value={achLast4}
              onChange={(e) => setAchLast4(e.target.value)}
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="ACH plaintext (routing+account)"
              value={achPlaintext}
              onChange={(e) => setAchPlaintext(e.target.value)}
              helperText="Sent to peo-lockdown when configured; otherwise paste a pre-created token below."
            />
            <TextField
              label="ACH vault token (optional)"
              value={achToken}
              onChange={(e) => setAchToken(e.target.value)}
            />
            <Button
              disabled={
                busy || achLast4.length !== 4 || (!achPlaintext && !achToken)
              }
              onClick={() =>
                run(async () => {
                  const res = await saveFoundationACH(id, {
                    last4: achLast4,
                    plaintext: achPlaintext || undefined,
                    token: achToken || undefined,
                  });
                  if (res.token) setAchToken(res.token);
                })
              }
            >
              Save ACH
            </Button>
            <Divider />
            <Typography variant="h6">Final IO approval</Typography>
            <TextField
              label="Installment count"
              type="number"
              value={installmentCount}
              onChange={(e) => setInstallmentCount(e.target.value)}
            />
            <TextField
              label="Payment date 1"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
            />
            {Number(installmentCount) > 1 && (
              <TextField
                label="Payment date 2"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={date2}
                onChange={(e) => setDate2(e.target.value)}
              />
            )}
            <TextField
              label="Payee name"
              value={payeeName}
              onChange={(e) => setPayeeName(e.target.value)}
            />
            <Button
              variant="contained"
              disabled={busy || !date1 || !payeeName}
              onClick={() => {
                const count = Number(installmentCount) || 1;
                const amounts =
                  count === 1
                    ? [amount]
                    : [Math.round((amount / 2) * 100) / 100, 0];
                if (count === 2) {
                  amounts[1] = Math.round((amount - amounts[0]) * 100) / 100;
                }
                const dates = [date1, ...(count > 1 && date2 ? [date2] : [])];
                return run(() =>
                  finalApproveFoundationApplication(id, {
                    installment_count: count,
                    amounts,
                    scheduled_dates: dates,
                    payment_method: achToken ? 'ach' : 'check',
                    payee: {
                      payee_type: 'institution',
                      name: payeeName,
                      ach_lockdown_token: achToken || undefined,
                      ach_last4: achToken ? achLast4 : undefined,
                    },
                  })
                );
              }}
            >
              Final approve & schedule payments
            </Button>
          </Stack>
        )}
    </Container>
  );
}
